/**
 * Centralised environment variable access for the Public Map (Vite).
 *
 * Every VITE_* var used in the app must be listed here.
 * No hardcoded fallback URLs — all values come from Vercel env vars.
 */

const meta = (import.meta as any).env ?? {};

/** Mapbox GL requires a non-empty token even for self-hosted styles. */
const MAPBOX_CI_FALLBACK = 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

export const API_URL: string = meta.VITE_API_URL ?? '';
export const TILES_URL: string = meta.VITE_TILES_URL || API_URL;

export function getApiRoot(): string {
  return (API_URL || '').replace(/\/$/, '');
}

/** Base URL for vector tile requests — prefers VITE_TILES_URL, then API, then same origin (Vite /tiles proxy). */
export function getTileRoot(): string {
  const configured = (TILES_URL || '').replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
}
export const MAPBOX_TOKEN: string =
  meta.VITE_MAPBOX_TOKEN || (meta.VITE_PLAYWRIGHT_TEST === 'true' ? MAPBOX_CI_FALLBACK : '');
/** True when the map app is built/served for Playwright (see playwright.config.ts). */
export const IS_PLAYWRIGHT_TEST = meta.VITE_PLAYWRIGHT_TEST === 'true';
export const MARKETING_URL: string = meta.VITE_MARKETING_URL ?? '';
export const CONSOLE_URL: string = meta.VITE_CONSOLE_URL ?? '';

const DEFAULT_CONTRIBUTOR_TERMS_DRIVE_ID = '112LV8QXHO6MT-OM92QE4r1gylO6I1dl5';

function resolveContributorTermsUrl(fullUrl?: string, driveFileId?: string): string {
  const trimmed = (fullUrl ?? '').trim();
  if (trimmed) return trimmed;
  const id = (driveFileId ?? '').trim() || DEFAULT_CONTRIBUTOR_TERMS_DRIVE_ID;
  return `https://drive.google.com/file/d/${id}/view?usp=sharing`;
}

/** Google Drive (or any public) URL for contributor terms PDF. */
export const CONTRIBUTOR_TERMS_URL: string = resolveContributorTermsUrl(
  meta.VITE_CONTRIBUTOR_TERMS_URL,
  meta.VITE_CONTRIBUTOR_TERMS_DRIVE_ID,
);
/** Email for contributor onboarding inquiries (mailto). */
export const CONTRIBUTOR_ONBOARD_EMAIL: string = meta.VITE_CONTACT_EMAIL ?? 'hello@coordal.com';

// Version + deploy traceability (injected at build time via vite.config.js)
export const APP_VERSION: string = meta.VITE_APP_VERSION ?? '4.3.0';
export const GIT_COMMIT_SHA: string = meta.VITE_GIT_COMMIT_SHA ?? '';
export const VERSION_DISPLAY: string = APP_VERSION;

/**
 * Temporary QA flag: allow live GPS trace / contributions outside Ghana bounds.
 * Set VITE_ALLOW_FIELD_MAPPING_OUTSIDE_GHANA=true locally — never in production.
 */
export const ALLOW_FIELD_MAPPING_OUTSIDE_GHANA: boolean =
  meta.VITE_ALLOW_FIELD_MAPPING_OUTSIDE_GHANA === 'true';
